/**
 * Módulo de hallazgos — CRUD, fotos, generación de OT y cierre
 */

import { createCrudMethods } from '../base/crudMethods';

/**
 * Crea el módulo de hallazgos
 *
 * @param {Object} deps - Dependencias inyectadas
 * @param {Function} deps.authenticatedRequest - Función de request autenticada
 * @param {Function} deps.authenticatedFormDataRequest - Función para uploads
 * @returns {Object} Métodos del módulo hallazgos
 */
export function createHallazgosModule({ authenticatedRequest, authenticatedFormDataRequest }) {
  return {
    ...createCrudMethods(
      {
        endpoint: '/api/hallazgos',
        entityName: 'hallazgo',
        entityNamePlural: 'hallazgos'
      },
      { authenticatedRequest }
    ),

    /**
     * Obtiene hallazgos propios del usuario (abiertos, sin OT)
     *
     * @param {Object} [params] - Parámetros de consulta
     * @returns {Promise<{success: boolean, data: Array, pagination: Object}>}
     */
    async getMisHallazgos(params = {}) {
      const qs = new URLSearchParams(params).toString();
      return await authenticatedRequest(qs ? `/api/hallazgos/mis-hallazgos?${qs}` : '/api/hallazgos/mis-hallazgos');
    },

    /**
     * Genera una OT desde un hallazgo
     *
     * @param {number} id - ID del hallazgo
     * @param {Object} data - { tipo_mantenimiento_id, fecha_programada, prioridad, descripcion_ot, plantilla_mantenimiento_id, usuario_asignado_id }
     * @returns {Promise<{success: boolean, data: {orden_trabajo_id: number, codigo_ot: string}}>}
     */
    async generarOTDesdeHallazgo(id, data) {
      return await authenticatedRequest(`/api/hallazgos/${id}/generar-ot`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Asocia un hallazgo a una OT existente
     *
     * @param {number} id - ID del hallazgo
     * @param {Object} data - { orden_trabajo_id }
     * @returns {Promise<{success: boolean, data: {orden_trabajo_id: number, codigo_ot: string}}>}
     */
    async asociarOTAHallazgo(id, data) {
      return await authenticatedRequest(`/api/hallazgos/${id}/asociar-ot`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Cierra un hallazgo sin OT
     *
     * @param {number} id - ID del hallazgo
     * @param {Object} data - { motivo_cierre }
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async cerrarHallazgo(id, data) {
      return await authenticatedRequest(`/api/hallazgos/${id}/cerrar`, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    },

    /**
     * Obtiene fotos de un hallazgo
     *
     * @param {number} id - ID del hallazgo
     * @returns {Promise<{success: boolean, data: Array}>}
     */
    async getHallazgoFotos(id) {
      return await authenticatedRequest(`/api/hallazgos/${id}/fotos`);
    },

    /**
     * Vincula una foto existente de galería a un hallazgo
     *
     * @param {number} id - ID del hallazgo
     * @param {Object} data - { foto_galeria_id }
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async vincularFotoAHallazgo(id, data) {
      return await authenticatedRequest(`/api/hallazgos/${id}/fotos`, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Sube una foto directa a un hallazgo
     *
     * @param {number} id - ID del hallazgo
     * @param {FormData} formData - Debe contener: archivo, descripcion (opcional)
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async uploadFotoHallazgo(id, formData) {
      return authenticatedFormDataRequest(`/api/hallazgos/${id}/fotos/upload`, {
        method: 'POST',
        body: formData
      });
    },

    /**
     * Desvincula una foto de un hallazgo
     *
     * @param {number} hallazgoId - ID del hallazgo
     * @param {number} fotoId - ID de la foto_galeria
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async desvincularFotoDeHallazgo(hallazgoId, fotoId) {
      return await authenticatedRequest(`/api/hallazgos/${hallazgoId}/fotos/${fotoId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Descarga imagen completa de una foto de hallazgo
     *
     * @param {number} hallazgoId - ID del hallazgo
     * @param {number} fotoId - ID de la foto_galeria
     * @returns {Promise<Response>}
     */
    async downloadFotoHallazgo(hallazgoId, fotoId) {
      return authenticatedFormDataRequest(`/api/hallazgos/${hallazgoId}/fotos/${fotoId}/download`, {
        responseType: 'blob'
      });
    },

    /**
     * Descarga thumbnail de una foto de hallazgo
     *
     * @param {number} hallazgoId - ID del hallazgo
     * @param {number} fotoId - ID de la foto_galeria
     * @returns {Promise<Response>}
     */
    async downloadThumbnailHallazgo(hallazgoId, fotoId) {
      return authenticatedFormDataRequest(`/api/hallazgos/${hallazgoId}/fotos/${fotoId}/thumbnail`, {
        responseType: 'blob'
      });
    },

    /**
     * Obtiene estadísticas de hallazgos
     *
     * @returns {Promise<{success: boolean, data: Object}>}
     */
    async getHallazgoStats() {
      return await authenticatedRequest('/api/hallazgos/stats/resumen');
    }
  };
}
